import {
  createBaseThemeOptions,
  createUnifiedTheme,
  defaultTypography,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';
import type { UnifiedThemeOptions } from '@backstage/theme';
import {
  vaporBlue,
  vaporBlueDark,
  vaporBlueLight,
  vaporBorderLight,
  vaporBorderStrong,
  vaporError,
  vaporInk,
  vaporInkSubtle,
  vaporSuccess,
  vaporSurface,
  vaporSurfaceAlt,
  vaporWarning,
  vaporWarningDark,
} from './vaporPalette';

/**
 * `UserListPicker` is still styled with
 * `makeStyles(..., { name: 'CatalogReactUserListPicker' })`, so this override is
 * applied at runtime. It cannot be expressed inline though:
 * `@backstage/plugin-catalog-react` registers the class key through the
 * `OverrideComponentNameToClassKeys` augmentation, but MUI's `Components`
 * interface derives its members from a mapped type over that interface, and TS
 * resolves those members before the catalog-react augmentation is merged — so
 * only the `@backstage/core-components` keys end up visible. Keeping the block
 * out of the main literal avoids the excess-property check on a key that is
 * valid in practice.
 */
const catalogReactOverrides = {
  CatalogReactUserListPicker: {
    styleOverrides: {
      groupWrapper: {
        margin: '8px 8px 8px 8px',
      },
      title: {
        margin: '8px 8px 0px 0px',
      },
    },
  },
} as UnifiedThemeOptions['components'];

export const vaporTheme = createUnifiedTheme({
  defaultPageTheme: 'home',
  fontFamily: 'Cairo, sans-serif',
  components: {
    BackstageHeader: {
      styleOverrides: {
        header: {
          width: 'auto',
          backgroundColor: vaporInk,
          backgroundImage: "none",
          padding: "12.5px",
        },
        title: {
          fontWeight: 700,
          fontSize: "20px",
        },
        subtitle: {
          fontSize: "16px",  
          fontWeight: 400,
          marginTop: "0px"
        }
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          paddingTop: "0px",
        }
      },
    },
    BackstageContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: vaporSurface,
          paddingTop: theme.spacing(2),
          paddingBottom: theme.spacing(2),
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
          [theme.breakpoints.up('sm')]: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
          },
        }),
      },
    },
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          // width: "224px !important",
          // `backgroundColor` removed: `BackstageSidebar`'s own `drawer` rule
          // is `background: theme.palette.navigation.background`, which now
          // carries the same colour.
        },
        // drawerOpen: {
        //   width: "224px !important",
        // }
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          // `color` removed: `BackstageSidebarItem`'s own root rule is
          // `color: theme.palette.navigation.color`.
          fontFamily: 'Cairo, sans-serif',
          borderRadius: "3px",
          width: "94% !important",
          marginLeft: "auto !important",
          marginRight: "auto !important",
          height: "36px",
          marginBottom: "2px",
          // '&[data-testid="item-with-submenu"], &[data-testid="item-with-submenu"] .MuiListItemButton-root': {
          //   width: '100% !important',
          // },
          // '&[data-testid="item-with-submenu"]:hover, &[data-testid="item-with-submenu"] .MuiListItemButton-root:hover': {
          //   width: '100% !important',
          //   marginLeft: 'auto',
          //   marginRight: 'auto',
          //   backgroundColor: 'transparent',
          // },
          '&.Mui-selected': {
            backgroundImage: `linear-gradient(315deg, ${vaporBlueLight}, ${vaporBlue}) !important`,
            color: `${vaporSurface} !important`,
            fontWeight: 500,
            textShadow: "1px 1px 0 rgba(21,41,53,0.24)",
            width: "94% !important",
            marginLeft: "auto !important",
            marginRight: "auto !important",
          },
          '&:hover': {
            backgroundImage: `linear-gradient(315deg, ${vaporBlueLight}, ${vaporBlue}) !important`,
            // width: "94% !important",
            // marginLeft: "auto !important",
            // marginRight: "auto !important",
          },
        },
        selected: {
          backgroundImage: `linear-gradient(315deg, ${vaporBlueLight}, ${vaporBlue}) !important`,
          // width: "94% !important",
          // marginLeft: "auto !important",
          // marginRight: "auto !important",
          // borderRadius: "3px",
        },
        iconContainer: {
          width: "50px",
          marginLeft: "0px",
          marginRight: "-5px",
        },
        secondaryAction: ({ theme }) => ({
          width: theme.spacing(3),
          textAlign: 'center',
          marginRight: theme.spacing(1),
        }),
        expandButton: {
          height: 20,
          width: 20,
          backgroundColor: "transparent",
          '&:hover': {
            backgroundColor: '#808080',
          },
        },
        // arrows: {
        //   '&:hover': {
        //     bgColor: '#808080',
        //   },
        // },
      },
    },
    // BackstageSidebarSubmenu: {
    //   styleOverrides: {
    //     root: ({ theme }) => ({
    //       zIndex: 1000,
    //       position: 'relative',
    //       overflow: 'visible',
    //       width: theme.spacing(7) + 1,
    //     }),
    //     drawer: {
    //       backgroundColor: "#152935",
    //       // Assicurati che il drawer del sottomenu sia allineato al tema
    //       borderRight: '1px solid #C9D9E8',
    //     },
    //   },
    // },
    // BackstageSidebarSubmenuItem: {
    //   styleOverrides: {
    //     item: {
    //       color: '#005075 !important',
    //       '&:hover': {
    //         backgroundColor: '#005075 !important',
    //         color: '#FFFFFF !important',
    //       },
    //     },
    //     selected: {
    //       backgroundColor: '#005075 !important',
    //       color: '#FFFFFF !important',
    //     },
    //   },
    // },
    ...catalogReactOverrides,
    MuiButton: {
      styleOverrides: {
        // root: {
        //   "&:hover": {
        //     // backgroundColor: "rgb(157, 166, 173) !important",
        //     backgroundColor: "#FFFFFF !important",
        //   }
        // },
        containedPrimary: {
          // No `textShadow`. It used to carry
          // `1px 1px 0 rgba(21,41,53,0.24)`, which at the sizes a button label
          // actually gets read as a muddier blue rather than as depth. The
          // Backstage UI primary rule in vapor-bui.css does not paint one
          // either, so the two halves agree.
          //
          // The sidebar selected item and `MuiStepIcon.text` keep theirs: both
          // put white on a full gradient band, which is where the shadow was
          // doing something.
          backgroundImage: `linear-gradient(315deg, ${vaporBlueLight}, ${vaporBlue})`,
          fontWeight: 500,
          boxShadow: "none",
        },
        textPrimary: {
          // `color` removed: MUI's own `textPrimary` is
          // `color: theme.palette.primary.main`, on both v4 and v5.
          fontWeight: 500,
          letterSpacing: "0.2px" 
        },
        outlinedPrimary: {
          // `borderColor` stays. MUI outlines with
          // `alpha(theme.palette.primary.main, 0.5)`, so dropping this would
          // fade the border to 50%. `color`, on the other hand, is already
          // `palette.primary.main`.
          borderColor: vaporBlue,
          fontWeight: 500,
          borderRadius: 0
        }
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        text: {
          textShadow: "1px 1px 0 rgba(21,41,53,0.24)",
          fontSize: "14px",
          fontWeight: 700
        }
      }
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          "&.Mui-active": {
            color: vaporBlueDark,
            fontWeight: 600,
          },
        },
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          // `backgroundColor` removed: MUI's own `TabsIndicator` uses
          // `palette.primary.main` for the default `indicatorColor="primary"`.
          height: "4px"
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textAlign: "center",
          minWidth: "100px !important",
          backgroundColor: `${vaporSurface} !important`,
          borderBottom: `solid 1px ${vaporBorderLight}`,
          "&.Mui-selected": {
            backgroundColor: `${vaporSurfaceAlt} !important`,
            backgroundImage: "none !important",
          },
          "&:hover": {
            backgroundColor: `${vaporBlue} !important`,
            color: `${vaporSurface} !important`,
          },
        }
      }
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        defaultTab: {
          color: vaporInkSubtle,
        },
        selected: {
          backgroundColor: vaporSurfaceAlt,
          color: vaporBlue,
          borderBottomColor: vaporBlue,
        },
      }
    },
    BackstageCardTab: {
      styleOverrides: {
        root: {
          color: vaporInkSubtle,
        },
        selected: {
          color: vaporBlue,
          borderBottomColor: vaporBlue
        }
      }
    },
    BackstageTabbedCard: {
      styleOverrides: {
        indicator: {
          backgroundColor: vaporBlue,
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          // boxShadow: "none",
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        title: {
          fontSize: '24px',
          color: vaporBlueDark,
          lineHeight: "24px",
          fontWeight: 600,
        }
      }
    },
    /**
     * Card titles carry three single-class rules at once, all of the same
     * specificity, so which one wins comes down to JSS injection order rather
     * than to anything we control:
     *
     *   .MuiTypography-h5                  16px, weight 600
     *   .MuiCardHeader-title               24px, #005075, weight 600
     *   .BackstageInfoCard-headerTitle     weight `typography.fontWeightBold`
     *
     * `InfoCard` reaches the third one by passing `classes={{ title }}` down to
     * `CardHeader`, so only the cards built on `InfoCard` had it — which is why
     * a group's cards came out at 700 while a module's About card, a plain
     * `Card`, came out at 600. Same family, same size, same colour: only the
     * weight moved, which reads as a different font.
     *
     * Restating the weight here makes all three rules agree, so the ordering
     * stops mattering. 600 is the target because it is what the Backstage UI
     * card titles use.
     */
    BackstageInfoCard: {
      styleOverrides: {
        headerTitle: {
          fontWeight: 600,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '24px',
          color: vaporBlueDark,
          letterSpacing: '0',
          lineHeight: '16px',
          fontWeight: 600,
          textAlign: 'left',
        },
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        dividers: {
          borderTopColor: vaporBlue,
          borderTopWidth: "2px",
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        elevation2: {
          borderRadius: 0,
          boxShadow: "none",
        }
      }
    },
    BackstageTableHeader: {
      styleOverrides: {
        header: {
          fontSize: "14px",
          color: `${vaporBlueDark} !important`,
          lineHeight: "20px",
          fontFamily: "'Cairo', sans-serif !important", 
          fontWeight: "bold",
          // textTransform: "capitalize !important",
          borderBottomColor: vaporBorderStrong,
          padding: '8px 16px',
        },
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: vaporSurface,
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: "'Cairo', sans-serif !important",
        },
        body: {
          borderBottom: `1px solid ${vaporBorderStrong}`,
          fontSize: '14px',
          color: vaporInk,
          lineHeight: '18px',
          fontFamily: 'Roboto',
          fontWeight: 400,
        }
      }
    },
    BackstageSelectInputBase: {
      styleOverrides: {
        input: {
          borderRadius: 0,
          borderColor: vaporBorderLight,
          backgroundColor: vaporSurfaceAlt,
          color: vaporInk,
        },
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: "3px",
          "&:hover": {
            backgroundColor: `${vaporBorderStrong} !important`,
            color: "white",
            "& .MuiListItemIcon-root": {
              color: "white",
            },
          },
          "&.Mui-selected": {
            backgroundImage: `linear-gradient(315deg, ${vaporBlueLight}, ${vaporBlue})`,
          },
        },
      }
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          whiteSpace: "normal"
        }
      }
    },
    // MuiToggleButton: {
    //   styleOverrides: {
    //     root: {
    //       "&.Mui-selected": {
    //         color: "#FFFFFF !important",
    //       },
    //     },
    //   }
    // },
    BackstageItemCardHeader: {
      styleOverrides: {
        root: {
          color: vaporSurface,
        },
      }
    },
  },
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      /**
       * Everything below used to be left at the stock Backstage values, with
       * the Vapor colours applied per component in `components` instead. That
       * is why Material UI controls that this theme does not override — every
       * checkbox, radio, switch, slider, progress bar and focused input —
       * rendered in Backstage blue `#1F5493`, and why nothing reached
       * Backstage UI at all.
       */
      primary: {
        main: vaporBlue,
        light: vaporBlueLight,
        dark: vaporBlueDark,
        contrastText: vaporSurface,
      },
      // Vapor has no dedicated secondary; the dark blue already used for
      // headings (MuiCardHeader, MuiDialogTitle) stands in for it. Its only
      // consumers today are two `Chip color="secondary"` in the onboarding
      // guide, which were rendering in the stock Material UI purple.
      secondary: {
        main: vaporBlueDark,
        contrastText: vaporSurface,
      },
      info: {
        main: vaporBlue,
        dark: vaporBlueDark,
      },
      success: {
        main: vaporSuccess,
      },
      warning: {
        main: vaporWarning,
      },
      error: {
        main: vaporError,
      },
      // Material UI deep-merges this over its own light defaults, so
      // `text.secondary` and `text.disabled` are preserved.
      text: {
        primary: vaporInk,
      },
      /*
       * `default` is white rather than the recessed grey, because the
       * `BackstageContent` override already paints every Material UI page
       * white and the Backstage UI entity page renders no `BackstageContent`.
       * Leaving them different made the page change colour depending on which
       * system drew it. `vaporSurfaceAlt` is still the recessed surface
       * elsewhere — selected tabs, `--bui-bg-neutral-2`.
       */
      background: {
        default: vaporSurface,
        paper: vaporSurface,
      },
      // Drives the StatusOK / StatusError / StatusWarning / StatusRunning
      // indicators in core-components. `pending` and `aborted` have no Vapor
      // equivalent and keep their stock values.
      status: {
        ...palettes.light.status,
        ok: vaporSuccess,
        warning: vaporWarning,
        error: vaporError,
        running: vaporBlue,
      },
      /**
       * `DismissableBanner`, which the error page and `plugin-kubernetes-react`
       * render. `text`, `link` and `closeButtonColor` are single values laid
       * over all three variants, so every background has to carry white: the
       * stock amber `#FF9800` was at 2.16:1 and the stock black link failed on
       * two backgrounds out of three. White is the only choice that clears AA
       * on all three, with the hover underline left to signal the link.
       */
      banner: {
        info: vaporBlueDark,
        error: vaporError,
        warning: vaporWarningDark,
        text: vaporSurface,
        link: vaporSurface,
        closeButtonColor: vaporSurface,
      },
      // The strongest text colour: WarningPanel, the Table header and Gauge.
      textContrast: vaporInk,
      // Code snippet line numbers and the scrollbar thumb.
      textVerySubtle: vaporBorderLight,
      /*
       * Deliberately left at the stock values: nothing reads them. Checked
       * across `@backstage/*`, `@backstage-community/*`, `@drodil/*` and this
       * repository — `errorBackground`, `warningBackground`, `infoBackground`,
       * `errorText`, `infoText`, `warningText`, `gold` and `pinSidebarButton`
       * have zero consumers, and `bursts` is deprecated upstream. Setting them
       * would be dead weight to maintain.
       */
      textSubtle: vaporInkSubtle,
      border: vaporBorderStrong,
      divider: vaporBorderLight,
      // Stock value is `#FFFBCC`, a pale yellow. Backstage UI maps this onto
      // `--bui-ring`, the focus outline, where a pale yellow on white is not a
      // visible focus indicator.
      highlight: vaporBlue,
      link: vaporBlue,
      linkHover: vaporBlueDark,
      tabbar: {
        indicator: vaporBlue,
      },
      /**
       * `core-components` reads `palette.navigation.*` across six Sidebar
       * files, so leaving it at the stock dark grey is what forced the
       * `!important` rules in the `BackstageSidebarItem` overrides below.
       * Those overrides are deliberately left in place for now — the hover and
       * selected states use the Vapor gradient, which a palette entry cannot
       * express.
       */
      navigation: {
        ...palettes.light.navigation,
        background: vaporInk,
        indicator: vaporBlue,
        color: vaporSurface,
        selectedColor: vaporSurface,
        navItem: {
          hoverBackground: vaporBlueDark,
        },
        submenu: {
          background: vaporInk,
        },
      },
    },
    typography: {
      ...defaultTypography,
      htmlFontSize: 16,
      fontFamily: "Cairo, sans-serif",
      h1: {
        fontWeight: 700,
        fontSize: "42px",
        marginBottom: 10,
      },
      h2: {
        fontWeight: 700,
        fontSize: "34px",
        marginBottom: 10,
      },
      h3: {
        fontWeight: 700,
        fontSize: "24px",
        marginBottom: 10,
      },
      h4: {
        fontWeight: 600,
        fontSize: "20px",
        marginBottom: 10,
      },
      h5: {
        fontWeight: 600,
        fontSize: "16px",
        marginBottom: 10,
      },
    },
  }),
  pageTheme: {
    home: genPageTheme({ colors: ['#61BEEB', '#00A0DC'], shape: shapes.wave }),
    documentation: genPageTheme({
      colors: ['#EE3B7A', '#C40075'],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({ colors: ['#FFD540', '#FBB040'], shape: shapes.round }),
    service: genPageTheme({
      colors: ['#FBB040', '#F37053'],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: ['#61BEEB', '#00A0DC'],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: ['#EE3B7A', '#C40075'],
      shape: shapes.wave,
    }),
    other: genPageTheme({ colors: ['#0073C0', '#164194'], shape: shapes.wave }),
    app: genPageTheme({ colors: ['#EE3B7A', '#C40075'], shape: shapes.wave }),
    apis: genPageTheme({ colors: ['#FBB040', '#F37053'], shape: shapes.wave }),    
  },
});
